/**
 * Las piezas que comparten «Registrar cuenta por pagar» y «Registrar cuenta
 * por cobrar»: los atajos de texto y de fecha, y el resumen de la derecha.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ LAS DOS VENTANAS SE PARECEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Son la misma operación mirada desde los dos lados: lo que el negocio debe y
 * lo que le deben. Si una pregunta «¿Qué es?» y la otra «Concepto», o una pone
 * la fecha arriba y la otra abajo, quien usa las dos aprende dos pantallas en
 * vez de una. El dueño pidió el rediseño de la primera («muy informal») y la
 * segunda nueva, «de una forma muy buena»: se hicieron juntas y con las mismas
 * piezas.
 *
 * El RESUMEN es lo que más ayuda: dice en una frase lo que se va a registrar
 * («Pedro Pérez te deberá $50,00 desde hoy»). Antes de guardar se lee eso, no
 * se repasan ocho casillas.
 */

import type { ReactNode } from "react";
import { cx } from "@/components/ui";
import { fechaLocal } from "@/lib/fechas";

/** Botones pequeños que escriben un texto de ejemplo en la casilla. */
export function AtajosDeTexto({
  opciones,
  valor,
  onElegir,
  etiqueta = "Sugerencias",
}: {
  opciones: string[];
  valor: string;
  onElegir: (texto: string) => void;
  etiqueta?: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={etiqueta}>
      {opciones.map((o) => {
        const elegido = valor.trim().toLowerCase() === o.toLowerCase();
        return (
          <button
            key={o}
            type="button"
            onClick={() => onElegir(o)}
            aria-pressed={elegido}
            className={cx(
              "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
              elegido
                ? "border-accent bg-accent-soft text-accent"
                : "border-line text-muted hover:border-line-strong hover:text-ink",
            )}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

/** La fecha de dentro de `dias` días, en el calendario de este aparato. */
export function enDias(dias: number): string {
  const f = new Date();
  f.setDate(f.getDate() + dias);
  return fechaLocal(f);
}

/** Atajos de fecha: «Hoy», «En 15 días»… Marcan el que coincide con la casilla. */
export function AtajosDeFecha({
  opciones,
  valor,
  onElegir,
  etiqueta,
}: {
  opciones: { texto: string; fecha: string }[];
  valor: string;
  onElegir: (fecha: string) => void;
  etiqueta: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={etiqueta}>
      {opciones.map((o) => (
        <button
          key={o.texto}
          type="button"
          onClick={() => onElegir(o.fecha)}
          aria-pressed={valor === o.fecha}
          className={cx(
            "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
            valor === o.fecha
              ? "border-accent bg-accent-soft text-accent"
              : "border-line text-muted hover:border-line-strong hover:text-ink",
          )}
        >
          {o.texto}
        </button>
      ))}
    </div>
  );
}

/** «15 de octubre de 2026», o «hoy», para leer de corrido en el resumen. */
export function fechaEnPalabras(fecha: string): string {
  if (!fecha) return "";
  if (fecha === fechaLocal()) return "hoy";
  return new Date(`${fecha}T12:00:00`).toLocaleDateString("es-VE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * La tarjeta de la derecha: la cifra grande y, debajo, la frase de lo que se
 * va a registrar. Queda fija mientras se baja por el formulario.
 */
export function ResumenDeCuenta({
  rotulo,
  cifra,
  frase,
  lineas,
  nota,
  tono = "neutro",
}: {
  rotulo: string;
  cifra: string;
  /** Lo que se va a registrar, en una frase. Vacío mientras faltan datos. */
  frase?: ReactNode;
  lineas?: { rotulo: string; valor: ReactNode }[];
  nota?: ReactNode;
  tono?: "neutro" | "entra" | "sale";
}) {
  return (
    <aside className="rounded-[var(--radius-card)] border border-line bg-surface-2 p-4 @4xl:sticky @4xl:top-0">
      <p className="rotulo">{rotulo}</p>
      <p
        className={cx(
          "tabular mt-1 text-3xl font-semibold leading-tight",
          tono === "entra" ? "text-success" : tono === "sale" ? "text-danger" : "text-ink",
        )}
      >
        {cifra}
      </p>
      {frase && <p className="mt-2 text-sm leading-relaxed text-ink-soft">{frase}</p>}
      {lineas && lineas.length > 0 && (
        <dl className="mt-3 space-y-1.5 border-t border-line pt-3 text-xs">
          {lineas.map((l) => (
            <div key={l.rotulo} className="flex items-baseline justify-between gap-3">
              <dt className="text-muted">{l.rotulo}</dt>
              <dd className="min-w-0 truncate text-right font-medium text-ink-soft">{l.valor}</dd>
            </div>
          ))}
        </dl>
      )}
      {nota && <p className="mt-3 text-xs leading-relaxed text-muted">{nota}</p>}
    </aside>
  );
}

/** Lo que falta para poder registrar, dicho: «Falta: el cliente · el monto». */
export function LoQueFalta({ falta }: { falta: string[] }) {
  if (falta.length === 0) return null;
  return (
    <p className="order-last self-center text-center text-xs text-warning sm:order-first sm:mr-auto sm:text-left">
      Falta: {falta.join(" · ")}.
    </p>
  );
}
